import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
    CopyExistingPolicy, CopyPlanItem, CopyResult, EventProductsService, ZoneCatalog,
} from 'src/app/services/event-products/event-products.service';
import { ImportZone, normalizeText } from 'src/app/shared/product-import/product-import.model';

type Step = 'select' | 'copying' | 'done';

/**
 * Copiar productos de la zona actual a otras zonas del evento.
 *
 * Se eligen uno, varios o todos los productos (con buscador) y una o varias
 * zonas destino. Antes de copiar se ve qué se va a crear y qué ya existe en
 * cada destino; lo que ya existe se omite o se actualiza según la opción.
 * La copia clona el producto con sus presentaciones (una referencia se copia
 * como otra referencia al mismo producto original). Nada se escribe hasta
 * confirmar.
 */
@Component({
    selector: 'app-copy-products',
    templateUrl: './copy-products.component.html',
    styleUrls: ['./copy-products.component.scss'],
})
export class CopyProductsComponent implements OnInit {

    @Input() zone: ImportZone;
    @Input() eventKey: string = '';
    @Input() products: any[] = [];

    step: Step = 'select';
    error = '';

    zones: ImportZone[] = [];
    zonesLoaded = false;

    search = '';
    selected: { [id: string]: boolean } = {};
    selectedZones: { [zoneKey: string]: boolean } = {};

    copyInventory = true;
    onExisting: CopyExistingPolicy = 'skip';

    plan: CopyPlanItem[] = [];
    counts = { create: 0, update: 0, skip: 0 };
    /** Por producto: zonas donde ya existe ("Barra 2, Barra 3"), para no filtrar el plan en cada render. */
    private existsMap: { [id: string]: string } = {};

    progress = 0;
    progressText = '';
    result: CopyResult = { created: 0, updated: 0, skipped: 0 };

    private catalogs: { [zoneKey: string]: ZoneCatalog } = {};
    private loadingCatalog: { [zoneKey: string]: boolean } = {};

    constructor(
        private modalCtrl: ModalController,
        private service: EventProductsService,
        public auth: AuthService,
    ) { }

    async ngOnInit() {
        try {
            const all = await this.service.getEventZones(this.eventKey, this.zone);
            this.zones = all.filter(z => z.key !== this.zone.key);
        } catch (e) {
            console.error('No se pudieron leer las zonas del evento:', e);
            this.error = 'No se pudieron leer las zonas del evento.';
        }
        this.zonesLoaded = true;
    }

    close(data?: any) {
        this.modalCtrl.dismiss(data);
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  PRODUCTOS
    // ═══════════════════════════════════════════════════════════════════════

    /** Una referencia y el producto original comparten $key: se distinguen aquí. */
    idOf(product: any): string {
        return product.reference && product.reference.$key ? `ref:${product.reference.$key}` : String(product.$key);
    }

    get visibleProducts(): any[] {
        const term = normalizeText(this.search);
        if (!term) return this.products;
        return this.products.filter(p => {
            const haystack = normalizeText([p.name, p.productCode, p.supplier].join(' '));
            return term.split(' ').every(word => haystack.indexOf(word) >= 0);
        });
    }

    get selectedProducts(): any[] {
        return this.products.filter(p => this.selected[this.idOf(p)]);
    }

    get selectedCount(): number {
        return this.selectedProducts.length;
    }

    get allVisibleSelected(): boolean {
        const list = this.visibleProducts;
        return list.length > 0 && list.every(p => this.selected[this.idOf(p)]);
    }

    get someVisibleSelected(): boolean {
        return this.visibleProducts.some(p => this.selected[this.idOf(p)]);
    }

    // Ionic 5 emite ionChange también cuando el valor se escribe desde el
    // modelo, así que cada cambio vuelve a llamar aquí: si no cambia nada, no
    // se recalcula el plan.
    toggleProduct(product: any, checked: boolean) {
        const id = this.idOf(product);
        if (!!this.selected[id] === !!checked) return;
        this.selected = { ...this.selected, [id]: !!checked };
        this.recompute();
    }

    /** Aplica a la lista visible: con búsqueda activa solo marca lo filtrado. */
    toggleAll(checked: boolean) {
        const list = this.visibleProducts;
        if (list.every(p => !!this.selected[this.idOf(p)] === !!checked)) return;
        const next = { ...this.selected };
        list.forEach(p => next[this.idOf(p)] = !!checked);
        this.selected = next;
        this.recompute();
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  ZONAS DESTINO
    // ═══════════════════════════════════════════════════════════════════════

    get destinationZones(): ImportZone[] {
        return this.zones.filter(z => this.selectedZones[z.key]);
    }

    async toggleZone(zone: ImportZone, checked: boolean) {
        if (!!this.selectedZones[zone.key] === !!checked) return;
        this.selectedZones = { ...this.selectedZones, [zone.key]: !!checked };
        if (checked && !this.catalogs[zone.key] && !this.loadingCatalog[zone.key]) {
            this.loadingCatalog[zone.key] = true;
            try {
                this.catalogs[zone.key] = await this.service.getZoneCatalog(zone.key);
            } catch (e) {
                console.error('No se pudo leer la zona destino:', e);
                this.error = `No se pudo leer ${zone.name}. Intenta de nuevo.`;
                this.selectedZones = { ...this.selectedZones, [zone.key]: false };
            }
            this.loadingCatalog[zone.key] = false;
        }
        this.recompute();
    }

    isLoadingZone(zone: ImportZone): boolean {
        return !!this.loadingCatalog[zone.key];
    }

    catalogSize(zone: ImportZone): number {
        return this.catalogs[zone.key] ? this.catalogs[zone.key].count : -1;
    }

    setExisting(policy: CopyExistingPolicy) {
        this.onExisting = policy;
        this.recompute();
    }

    setCopyInventory(value: boolean) {
        this.copyInventory = !!value;
    }

    setSearch(value: string) {
        this.search = String(value || '');
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  PLAN
    // ═══════════════════════════════════════════════════════════════════════

    recompute() {
        const zones = this.destinationZones.filter(z => !!this.catalogs[z.key]);
        this.plan = this.service.planCopy(this.selectedProducts, zones, this.catalogs, this.onExisting);
        this.counts = { create: 0, update: 0, skip: 0 };
        const exists: { [id: string]: string[] } = {};
        this.plan.forEach(item => {
            this.counts[item.action] += 1;
            if (item.action === 'create') return;
            const id = this.idOf(item.product);
            (exists[id] = exists[id] || []).push(item.zone.name);
        });
        this.existsMap = {};
        Object.keys(exists).forEach(id => this.existsMap[id] = exists[id].join(', '));
    }

    /** "Barra 2, Barra 3": zonas destino donde el producto ya existe. */
    existsIn(product: any): string {
        return this.existsMap[this.idOf(product)] || '';
    }

    get canCopy(): boolean {
        return this.step === 'select' && this.counts.create + this.counts.update > 0
            && this.destinationZones.every(z => !!this.catalogs[z.key]);
    }

    get copyLabel(): string {
        const n = this.counts.create + this.counts.update;
        const zones = this.destinationZones.length;
        if (n === 0) return 'Copiar';
        return `Copiar ${n} ${n === 1 ? 'producto' : 'productos'} a ${zones} ${zones === 1 ? 'zona' : 'zonas'}`;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  COPIAR
    // ═══════════════════════════════════════════════════════════════════════

    async runCopy() {
        if (!this.canCopy) return;
        this.step = 'copying';
        this.progress = 0;
        this.progressText = 'Preparando…';
        this.error = '';

        const positions: { [zoneKey: string]: number } = {};
        Object.keys(this.catalogs).forEach(key => positions[key] = this.catalogs[key].count);

        try {
            this.result = await this.service.copyProducts(
                this.plan,
                { sourceZone: this.zone, eventKey: this.eventKey, copyInventory: this.copyInventory },
                positions,
                (done, total) => {
                    this.progress = total > 0 ? done / total : 1;
                    this.progressText = `${done} de ${total}`;
                },
            );
            this.step = 'done';
        } catch (e) {
            console.error('Falló la copia:', e);
            this.error = 'No se pudieron copiar todos los productos. Revisa la conexión e intenta de nuevo: '
                + 'lo que sí se copió aparecerá como existente.';
            // Se releen los destinos para reflejar lo que sí se escribió.
            this.catalogs = {};
            for (const zone of this.destinationZones) {
                try {
                    this.catalogs[zone.key] = await this.service.getZoneCatalog(zone.key);
                } catch (err) {
                    this.selectedZones = { ...this.selectedZones, [zone.key]: false };
                }
            }
            this.recompute();
            this.step = 'select';
        }
    }

    finish() {
        this.close({
            copied: true,
            created: this.result.created,
            updated: this.result.updated,
            skipped: this.result.skipped,
            zones: this.destinationZones.map(z => z.name),
        });
    }
}
